import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PortalPageHead } from '@/components/portal/PortalChrome';
import { PortalIcon } from '@/components/portal/PortalIcon';
import {
  useAcknowledgePortalConsentDocument,
  useAnswerConsentRequestItem,
  usePortalConsentDocumentPrompts,
  usePortalConsentInbox,
  type PortalConsentDocumentPromptRow,
  type PortalConsentInboxRow,
} from '@/hooks/useConsentRequests';
import { formatPortalShortDate } from '@/lib/portalFormat';
import { PORTAL_CONSENT_TYPE_LABELS, groupPortalInbox, portalAnswerLabels } from '@/lib/consent/portalConsent';
import { usePortalActiveClinic } from '@/lib/portalActiveClinic';

export function PortalConsents() {
  const [params] = useSearchParams();
  const highlight = params.get('request');
  const { activeClinicId, clinics } = usePortalActiveClinic();
  const activeClinic = clinics.find((clinic) => clinic.clinic_id === activeClinicId);
  const inbox = usePortalConsentInbox(true, activeClinicId);
  const prompts = usePortalConsentDocumentPrompts(true, activeClinicId);
  const answer = useAnswerConsentRequestItem();
  const acknowledge = useAcknowledgePortalConsentDocument();
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [doneText, setDoneText] = useState<string | null>(null);

  const groups = groupPortalInbox(inbox.data ?? []);
  const promptRows = prompts.data ?? [];
  const loading = inbox.isLoading || prompts.isLoading;
  const failed = inbox.error || prompts.error;

  useEffect(() => {
    if (!highlight || loading) return;
    document.getElementById(`consent-request-${highlight}`)?.scrollIntoView({ block: 'start' });
  }, [highlight, loading]);

  const onAnswer = async (row: PortalConsentInboxRow, decision: 'given' | 'refused') => {
    setBusyKey(row.item_id);
    setErrorText(null);
    try {
      await answer.mutateAsync({ itemId: row.item_id, decision, relationId: row.relation_id });
      setDoneText('Tak. Dit svar er gemt, og klinikken kan se det.');
    } catch (err) {
      console.error('answer_consent_request_item failed', err);
      setErrorText('Dit svar kunne ikke gemmes. Anmodningen kan være udløbet eller annulleret. Prøv igen.');
    } finally {
      setBusyKey(null);
    }
  };

  const onAcknowledge = async (row: PortalConsentDocumentPromptRow) => {
    if (row.consent_type !== 'privacy_notice' && row.consent_type !== 'portal_terms') return;
    const key = `${row.patient_id}:${row.consent_type}`;
    setBusyKey(key);
    setErrorText(null);
    try {
      await acknowledge.mutateAsync({
        patientId: row.patient_id,
        consentType: row.consent_type,
        textVersionId: row.text_version_id,
      });
      setDoneText('Tak. Det er registreret.');
    } catch (err) {
      console.error('record_consent (portal document) failed', err);
      setErrorText('Det kunne ikke gemmes. Prøv igen.');
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <>
      <PortalPageHead
        title="Samtykker"
        subtitle={
          activeClinic
            ? `${activeClinic.clinic_name} beder dig tage stilling. Du bestemmer selv, og et nej får ikke betydning for din behandling.`
            : 'Klinikken beder dig tage stilling. Du bestemmer selv, og et nej får ikke betydning for din behandling.'
        }
      />

      {errorText ? (
        <div className="notice amber" role="alert" style={{ marginBottom: 16 }}>
          <PortalIcon name="info" size="sm" />
          <span>{errorText}</span>
        </div>
      ) : doneText ? (
        <div className="notice teal" role="status" style={{ marginBottom: 16 }}>
          <PortalIcon name="check" size="sm" />
          <span>{doneText}</span>
        </div>
      ) : null}

      {loading ? (
        <section className="card">
          <p className="help">Henter samtykker…</p>
        </section>
      ) : failed ? (
        <section className="card">
          <p className="help">Samtykkerne kunne ikke hentes. Prøv igen.</p>
        </section>
      ) : groups.length === 0 && promptRows.length === 0 ? (
        <section className="card">
          <p className="help">Du har ingen samtykker eller vilkår, der venter på dit svar.</p>
        </section>
      ) : (
        <>
          {promptRows.map((row) => {
            const labels = portalAnswerLabels(row.consent_type);
            const key = `${row.patient_id}:${row.consent_type}`;
            return (
              <section key={key} className="card" style={{ marginBottom: 16 }}>
                <div className="card-h">
                  <h3>{PORTAL_CONSENT_TYPE_LABELS[row.consent_type]}</h3>
                  <div className="r">
                    <span className="chip chip-amber">Ny version</span>
                  </div>
                </div>
                <p className="help">
                  {row.clinic_name} · Version {row.version_label}
                </p>
                <ConsentText body={row.body} />
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={busyKey !== null}
                    onClick={() => void onAcknowledge(row)}
                  >
                    {busyKey === key ? 'Gemmer…' : labels.yes}
                  </button>
                </div>
              </section>
            );
          })}

          {groups.map((group) => (
            <section
              key={`${group.requestId}:${group.onBehalfOfOther ? group.patientName : 'self'}`}
              id={`consent-request-${group.requestId}`}
              className="card"
              style={{ marginBottom: 16 }}
            >
              <div className="card-h">
                <h3>{group.clinicName} beder om dit svar</h3>
                <div className="r">
                  <span className="chip chip-amber">Svar senest {formatPortalShortDate(group.expiresAt)}</span>
                </div>
              </div>
              {group.onBehalfOfOther ? (
                <div className="notice neutral" style={{ marginBottom: 12 }}>
                  <PortalIcon name="user" size="sm" />
                  <span>Du svarer på vegne af {group.patientName}.</span>
                </div>
              ) : null}
              {group.items.map((row) => {
                const labels = portalAnswerLabels(row.consent_type);
                const busy = busyKey === row.item_id;
                return (
                  <div key={row.item_id} style={{ padding: '16px 0', borderTop: '1px solid var(--line)' }}>
                    <div className="t" style={{ fontWeight: 600 }}>
                      {PORTAL_CONSENT_TYPE_LABELS[row.consent_type]}
                    </div>
                    <p className="help">Version {row.version_label}</p>
                    <ConsentText body={row.body} />
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={busyKey !== null}
                        onClick={() => void onAnswer(row, 'given')}
                      >
                        {busy ? 'Gemmer…' : labels.yes}
                      </button>
                      {labels.no ? (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          disabled={busyKey !== null}
                          onClick={() => void onAnswer(row, 'refused')}
                        >
                          {labels.no}
                        </button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </section>
          ))}
        </>
      )}

      <p className="help" style={{ marginTop: 16 }}>
        Vil du ændre eller trække et samtykke tilbage, så{' '}
        <Link to="/portal/messages" className="link">
          skriv til klinikken
        </Link>
        .
      </p>
    </>
  );
}

function ConsentText({ body }: { body: string }) {
  return (
    <div
      className="help"
      style={{
        whiteSpace: 'pre-wrap',
        maxHeight: 320,
        overflowY: 'auto',
        marginTop: 12,
        padding: 12,
        border: '1px solid var(--line)',
        borderRadius: 12,
        background: 'var(--sunk)',
        color: 'var(--text)',
      }}
      tabIndex={0}
    >
      {body}
    </div>
  );
}
